import React, { useEffect, useState, useContext } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { db } from "../lib/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { LeagueContext } from "../context/LeagueContext";
import Layout from "../central/Layout";
import { PrimaryButton, SecondaryButton } from "../central/Button";
import NotificationPrompt from "../components/NotificationPrompt";
import InstallBanner from "../components/InstallBanner";
import styled from "styled-components";
import PageLoader from "../components/PageLoader";

// Per-user draft key helper (must match CreateLeague.js)
const DRAFT_KEY = (uid) => `createLeagueDraft_v1:${uid}`;

// Optional (set later if you want to link a PDF deck)
const MARKETING_DECK_URL = process.env.REACT_APP_MARKETING_DECK_URL || "";
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.medium};
  background-color: ${({ theme }) => theme.colors.background};
  min-height: 80vh;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const LeagueCard = styled.div`
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  border-left: 4px solid #50B4C8;
  border-radius: 8px;
  padding: 16px 20px;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: box-shadow 0.15s ease, transform 0.1s ease;
  &:hover {
    box-shadow: 0 3px 10px rgba(0,0,0,0.12);
    transform: translateY(-1px);
  }
`;

const LeagueCardName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const AdminPill = styled.span`
  background: #E07856;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 20px;
  letter-spacing: 0.02em;
  vertical-align: middle;
`;

const AvailabilityPill = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.02em;
  vertical-align: middle;
  background: ${({ $status }) =>
    $status === "available" ? "#e8f5e9" :
    $status === "unavailable" ? "#f5f5f5" :
    "#fff8e1"};
  color: ${({ $status }) =>
    $status === "available" ? "#2e7d32" :
    $status === "unavailable" ? "#888888" :
    "#f57c00"};
`;

const LeagueCardMeta = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textSecondary || "#666"};
  line-height: 1.4;
`;

const InfoCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  border-radius: 10px;
  padding: ${({ theme }) => theme.spacing.medium};
  background: #fafafa;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const InfoTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const StepList = styled.ol`
  margin: 0;
  padding-left: 1.1rem;
  color: inherit;
`;

const StepItem = styled.li`
  margin: 0.35rem 0;
  line-height: 1.35;
  color: inherit;
`;

const InlineLinks = styled.div`
  margin-top: ${({ theme }) => theme.spacing.small};
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  flex-wrap: wrap;
`;

const SmallLink = styled(Link)`
  text-decoration: underline;
`;

const ExternalLink = styled.a`
  text-decoration: underline;
`;

const TextAction = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
`;

const QuickGuideLink = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  color: #888888;
  font-size: 0.8rem;
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.large};

  @media (min-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ActionCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border || "#e6e6e6"};
  border-radius: 10px;
  padding: ${({ theme }) => theme.spacing.medium};
  background: #fff;
`;

const ActionTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const ActionBody = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary || "#666"};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  line-height: 1.35;
`;

function ResumeDraftButton({ uid }) {
  const navigate = useNavigate();
  if (!uid) return null;

  const hasDraft = !!localStorage.getItem(DRAFT_KEY(uid));
  if (!hasDraft) return null;

  return (
    <PrimaryButton onClick={() => navigate("/create")} style={{ marginLeft: 8 }}>
      Resume league draft
    </PrimaryButton>
  );
}

const Home = () => {
  const { currentUser } = useAuth();
  const { setCurrentLeagueId } = useContext(LeagueContext);
  const navigate = useNavigate();
  const location = useLocation();
  void location;
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);


  const goToDashboard = async (leagueID) => {
    try {
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { currentLeagueID: leagueID });
      }
      setCurrentLeagueId?.(leagueID);
      navigate(`/pickteam/${leagueID}`);
    } catch (error) {
      console.error("Error setting current league:", error);
    }
  };

  useEffect(() => {
    const fetchLeagues = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
  
      try {
        // Read the user's doc which contains leagueIDs and teams map
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
  
        if (!userSnap.exists()) {
          setLeagues([]);
          return;
        }
  
        const userData = userSnap.data() || {};
        const leagueIDs = Array.isArray(userData.leagueIDs) ? userData.leagueIDs : [];
        const teamsMap = userData.teams || {};
  
        if (leagueIDs.length === 0) {
          setLeagues([]);
          return;
        }
  
        // Fetch each league doc and build the list
        const leagueDetails = await Promise.all(
          leagueIDs.map(async (leagueID) => {
            const leagueSnap = await getDoc(doc(db, "leagues", leagueID));
            const leagueData = leagueSnap.exists() ? leagueSnap.data() : {};
            const leagueName = leagueData.name || "(Unnamed League)";
            const currentGW = leagueData.currentGW || null;
            const currentSeasonID = leagueData.currentSeasonID || null;
            const role = userData.roles?.[leagueID] || null;

            const teamID = teamsMap[leagueID] || null;
            let managerName = null;
            if (teamID) {
              const teamSnap = await getDoc(doc(db, "teams", teamID));
              if (teamSnap.exists()) {
                managerName = teamSnap.data().managerName || null;
              }
            }

            // Fetch deadline if we have enough info
            let deadline = null;
            if (currentSeasonID && currentGW) {
              try {
                const gwSnap = await getDoc(
                  doc(db, "leagues", leagueID, "seasons", currentSeasonID, "gameweeks", String(currentGW))
                );
                if (gwSnap.exists()) {
                  deadline = gwSnap.data().deadline || null;
                }
              } catch (_) {}
            }

            // Fetch current user's availability response for this GW
            let availabilityStatus = "not_responded";
            if (currentSeasonID && currentGW && currentUser) {
              try {
                const availRef = doc(
                  db,
                  "leagues", leagueID,
                  "seasons", currentSeasonID,
                  "gameweeks", String(currentGW),
                  "availability", "responses"
                );
                const availSnap = await getDoc(availRef);
                if (availSnap.exists()) {
                  const responses = availSnap.data()?.responses || {};
                  const userResponse = responses[currentUser.uid];
                  if (userResponse === "available") availabilityStatus = "available";
                  else if (userResponse === "unavailable") availabilityStatus = "unavailable";
                }
              } catch (_) {}
            }

            return { leagueID, teamID, leagueName, managerName, currentGW, currentSeasonID, deadline, role, availabilityStatus };
          })
        );
  
        setLeagues(leagueDetails);
      } catch (err) {
        console.error("Error fetching leagues:", err);
        setLeagues([]);
      } finally {
        setLoading(false);
      }
    };
  
    fetchLeagues();
  }, [currentUser]);

  return (
    <>
      <NotificationPrompt />
      {loading ? <PageLoader /> : null}
      {!loading && <Layout>
      <PageContainer>
        {!currentUser ? (
          <>
            <p>Welcome to ClubWeek! Please sign in or sign up</p>

            <InfoCard>
              <InfoTitle>How it works</InfoTitle>
              <StepList>
                <StepItem>
                  Join your league with a code, then pick a weekly team before the deadline
                </StepItem>
                <StepItem>
                  After matches, results are entered and points update automatically
                </StepItem>
              </StepList>
              <InlineLinks>
                <span style={{ opacity: 0.8 }}>
                  Need help?{' '}
                  <SmallLink to="/faq">FAQ</SmallLink>
                  {' · '}
                  <SmallLink to="/help">Help / Contact</SmallLink>
                </span>
              </InlineLinks>
            </InfoCard>

            <ButtonRow>
              <PrimaryButton onClick={() => navigate("/login")}>Login</PrimaryButton>
              <SecondaryButton onClick={() => navigate("/signup")}>Sign Up</SecondaryButton>
            </ButtonRow>
          </>
        ) : (
          <>
            {leagues.length === 0 ? (
              <>
                <p className="mb-1" style={{ fontWeight: 700 }}>
                  Welcome, {currentUser.displayName || currentUser.email}
                </p>

                <InfoCard>
                  <InfoTitle>Getting started</InfoTitle>
                  <StepList>
                    <StepItem>
                      <strong>Join a league</strong> with a code from your organizer
                    </StepItem>
                    <StepItem>
                      Or <strong>create a new league</strong> if you’re setting one up for your group
                    </StepItem>
                  </StepList>

                  <InlineLinks>
                    <span style={{ opacity: 0.8 }}>
                      Need help?{' '}
                      <SmallLink to="/faq">FAQ</SmallLink>
                      {' · '}
                      <SmallLink to="/help">Help / Contact</SmallLink>
                    </span>
                    {MARKETING_DECK_URL ? (
                      <ExternalLink
                        href={MARKETING_DECK_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Overview deck (PDF)
                      </ExternalLink>
                    ) : null}
                  </InlineLinks>
                </InfoCard>

                <TwoCol>
                  <ActionCard>
                    <ActionTitle>Join a league</ActionTitle>
                    <ActionBody>
                      Have a league code? <br/>Join in a few seconds and pick your team before the deadline
                    </ActionBody>
                    <PrimaryButton onClick={() => navigate("/join")}>Join League</PrimaryButton>
                  </ActionCard>

                  <ActionCard>
                    <ActionTitle>Create a league</ActionTitle>
                    <ActionBody>
                      Setting up a new league? <br/>Add players, rules, and get everything ready for week one
                    </ActionBody>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <SecondaryButton onClick={() => navigate("/create")}>Create New League</SecondaryButton>
                      <ResumeDraftButton uid={currentUser.uid} />
                    </div>
                  </ActionCard>
                </TwoCol>
              </>
            ) : (
              <>
                <p style={{ fontWeight: 400, fontSize: "0.9rem", color: "#888", marginBottom: 8 }}>
                  Welcome, {leagues.length > 0 && leagues[0].managerName
                    ? leagues[0].managerName
                    : currentUser.displayName || currentUser.email}
                </p>

                <div>
                  {leagues.map(({ leagueID, leagueName, managerName, currentGW, currentSeasonID, deadline, role, availabilityStatus }) => {
                    let deadlineStr = null;
                    if (deadline) {
                      try {
                        const d = deadline.toDate ? deadline.toDate() : new Date(deadline);
                        deadlineStr = d.toLocaleString(undefined, {
                          weekday: "short", month: "short", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        });
                      } catch (_) {}
                    }
                    return (
                      <LeagueCard key={leagueID} onClick={() => goToDashboard(leagueID)}>
                        <LeagueCardName>
                          {leagueName}
                          {role === "admin" && <AdminPill>Admin</AdminPill>}
                          <AvailabilityPill $status={availabilityStatus}>
                            {availabilityStatus === "available" ? "Playing" :
                             availabilityStatus === "unavailable" ? "Can't Make It" :
                             "Respond"}
                          </AvailabilityPill>
                        </LeagueCardName>
                        <LeagueCardMeta>
                          {currentGW && currentSeasonID
                            ? `${currentGW} · ${currentSeasonID}`
                            : managerName || null}
                          {deadlineStr && ` · Deadline: ${deadlineStr}`}
                        </LeagueCardMeta>
                      </LeagueCard>
                    );
                  })}
                </div>

                <InstallBanner />

                <div style={{ display: "flex", gap: "1rem", marginTop: 16, flexWrap: "wrap" }}>
                  <span
                    onClick={() => navigate("/join")}
                    style={{ color: "#888", fontSize: "0.85rem", textDecoration: "underline", cursor: "pointer" }}
                  >
                    Join another league
                  </span>
                  <span
                    onClick={() => navigate("/create")}
                    style={{ color: "#888", fontSize: "0.85rem", textDecoration: "underline", cursor: "pointer" }}
                  >
                    Create new league
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </PageContainer>
    </Layout>}
    </>
  );
};

export default Home;